/**
 * Keeps the free launch scarf honest in the cart.
 *
 * The scarf is a real line item priced at $0 by an automatic "Buy X get Y" discount, which only
 * applies while a qualifying bag is in the cart. Remove the bag, or cut its quantity, and Shopify
 * quietly re-prices the scarf to full price — leaving the shopper about to pay for something the
 * product page called free.
 *
 * This trims any scarf line back to the number actually being discounted, and removes it outright
 * when none are. It only ever touches lines carrying the offer's own marker property, so a scarf
 * the shopper deliberately bought is never removed.
 */

const GIFT_FLAG = '_scarf_offer';
// Matches ThemeEvents.cartUpdate in assets/events.js, spelled out so this file stays importless.
const CART_UPDATE = 'cart:update';
const SOURCE_ID = 'scarf-offer-guard';

class ScarfOfferGuard extends HTMLElement {
  #busy = false;

  connectedCallback() {
    document.addEventListener(CART_UPDATE, this.#onCartUpdate);
    this.#reconcile();
  }

  disconnectedCallback() {
    document.removeEventListener(CART_UPDATE, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    // Never react to our own corrections, or to the PDP component's add.
    const sourceId = event.detail?.sourceId;
    if (sourceId === SOURCE_ID || sourceId === 'scarf-offer') return;
    this.#reconcile();
  };

  async #reconcile() {
    if (this.#busy) return;
    this.#busy = true;

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
      const corrections = cart.items
        .filter((item) => item.properties?.[GIFT_FLAG])
        .map((item) => ({ item, freeQuantity: freeQuantityOf(item) }))
        .filter(({ item, freeQuantity }) => item.quantity > freeQuantity);

      if (!corrections.length) return;

      for (const { item, freeQuantity } of corrections) {
        await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: freeQuantity }),
        });
      }

      const refresh = new Event(CART_UPDATE, { bubbles: true });
      // Horizon's cart-items handler reads event.detail.data.sections and re-renders the section
      // from the server when it is absent, which is what we want here.
      refresh.detail = { resource: null, sourceId: SOURCE_ID, data: {} };
      document.dispatchEvent(refresh);
    } catch (error) {
      console.error('[scarf-offer-guard] could not reconcile the free scarf lines:', error);
    } finally {
      this.#busy = false;
    }
  }
}

/** How many units of a line the discount is actually covering. */
function freeQuantityOf(item) {
  const unitPrice = item.original_price;
  if (!unitPrice) return item.quantity;

  const discounted = (item.line_level_discount_allocations || []).reduce(
    (total, allocation) => total + allocation.amount,
    0
  );

  return Math.min(item.quantity, Math.floor(discounted / unitPrice));
}

if (!customElements.get('scarf-offer-guard')) {
  customElements.define('scarf-offer-guard', ScarfOfferGuard);
}
